import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../supabaseClient';
import SafeIcon from '../../common/SafeIcon';
import AuthGuard from '../../components/trainer/AuthGuard';
import OverviewSection from '../../components/trainer/OverviewSection';
import ClientsSection from '../../components/trainer/ClientsSection';
import PlansSection from '../../components/trainer/PlansSection';
import TasksSection from '../../components/trainer/TasksSection';
import PlanNotes from '../../components/common/PlanNotes';
import TemplatesSection from '../../components/trainer/TemplatesSection';
import MessagingSection from '../../components/trainer/MessagingSection';
import IntegrationsSection from '../../components/trainer/IntegrationsSection';
import PaymentsSection from '../../components/trainer/PaymentsSection';
import SettingsSection from '../../components/trainer/SettingsSection';
import ResourcesSection from '../../components/trainer/ResourcesSection';
import OwnerPreviewModal from '../../components/trainer/OwnerPreviewModal';

const TrainerDashboard = () => {
  const [profile, setProfile] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [unreadMap, setUnreadMap] = useState({});
  const [previewPlan, setPreviewPlan] = useState(null);
  const [previewTasks, setPreviewTasks] = useState([]);
  const [previewClient, setPreviewClient] = useState(null);

  const fetchProfile = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      const { data } = await supabase.from('trainer_profiles').select('*').eq('user_id', user.id).single();
      setProfile(data);
    }
  };

  const fetchUnreadCounts = useCallback(async () => {
    if (!profile) return;
    try {
      const { data: clients } = await supabase.from('training_clients').select('id').eq('trainer_id', profile.id);
      const clientIds = clients?.map(c => c.id) || [];
      if (!clientIds.length) return;

      const { data: plans } = await supabase.from('training_plans').select('id').in('client_id', clientIds);
      const planIds = plans?.map(p => p.id) || [];
      if (!planIds.length) return;

      const { data: messages } = await supabase.from('trainer_messages').select('plan_id').eq('is_read', false).eq('sender_type', 'owner').in('plan_id', planIds);
      
      const counts = {};
      messages?.forEach(m => {
        counts[m.plan_id] = (counts[m.plan_id] || 0) + 1;
      });
      setUnreadMap(counts);
    } catch (err) {
      console.error(err);
    }
  }, [profile]);

  useEffect(() => {
    fetchProfile();
  }, []);

  useEffect(() => {
    if (profile) {
      fetchUnreadCounts();
      const channel = supabase.channel('trainer_dashboard_messages')
        .on('postgres_changes', { event: '*', schema: 'public', table: 'trainer_messages' }, fetchUnreadCounts)
        .subscribe();
      return () => {
        supabase.removeChannel(channel);
      };
    }
  }, [profile, fetchUnreadCounts]);

  const handleRefresh = () => setRefreshTrigger(prev => prev + 1);

  const handlePlanSelect = (plan, taskId = null) => {
    setSelectedPlan(plan);
    setSelectedTaskId(taskId);
    setActiveTab('tasks');
  };

  const updateBadgeCount = (planId, count) => {
    setUnreadMap(prev => ({ ...prev, [planId]: count }));
  };

  const handlePreviewRequest = async (plan, tasks = [], client = null) => {
    setPreviewPlan(plan);
    setPreviewTasks(tasks);
    if (client) setPreviewClient(client);
    else if (plan?.client_id) {
      const { data } = await supabase.from('training_clients').select('*').eq('id', plan.client_id).single();
      setPreviewClient(data);
    }
  };

  const totalUnread = Object.values(unreadMap).reduce((a, b) => a + b, 0);

  const tabs = [
    { id: 'overview', icon: 'LayoutDashboard' },
    { id: 'clients', icon: 'Users' },
    { id: 'plans', icon: 'Calendar' },
    { id: 'tasks', icon: 'CheckSquare' },
    { id: 'notes', icon: 'FileText' },
    { id: 'templates', icon: 'Book' },
    { id: 'resources', icon: 'BookOpen' },
    { id: 'messaging', icon: 'MessageCircle' },
    { id: 'payments', icon: 'Award' },
    { id: 'settings', icon: 'Edit' }
  ];

  if (!profile) return null;

  return (
    <AuthGuard>
      <div className="min-h-screen bg-gray-50">
        <div className="bg-white border-b sticky top-0 z-10 flex space-x-8 max-w-7xl mx-auto px-4 overflow-x-auto scrollbar-hide">
          {tabs.map(t => (
            <button 
              key={t.id} 
              onClick={() => {
                setActiveTab(t.id);
                if (t.id !== 'tasks' && t.id !== 'notes') setSelectedTaskId(null);
              }}
              className={`py-4 px-1 border-b-2 font-black text-xs uppercase tracking-widest flex items-center gap-2 transition-all shrink-0 ${activeTab === t.id ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-gray-400 hover:text-gray-600'}`}
            >
              <SafeIcon name={t.icon === 'BookOpen' ? 'Book' : t.icon} className="w-4 h-4" />
              <div className="flex items-center gap-2">
                {t.id === 'notes' ? 'Notes' : t.id}
                {t.id === 'messaging' && totalUnread > 0 && (
                  <span className="bg-red-600 text-white text-[10px] px-1.5 py-0.5 rounded-full">{totalUnread}</span>
                )}
              </div>
            </button>
          ))}
        </div>

        <div className="max-w-7xl mx-auto px-4 py-8">
          {activeTab === 'overview' && (
            <OverviewSection 
              trainerId={profile.id} 
              onPlanSelect={handlePlanSelect} 
              refreshKey={refreshTrigger} 
              unreadMessagesCount={totalUnread}
            />
          )}
          {activeTab === 'clients' && (
            <ClientsSection 
              trainerId={profile.id} 
              onClientSelect={(c) => { setSelectedClient(c); setActiveTab('plans'); }} 
              onRefresh={handleRefresh}
            />
          )}
          {activeTab === 'plans' && (
            <PlansSection 
              trainerId={profile.id} 
              selectedClient={selectedClient}
              onPlanSelect={handlePlanSelect}
              onRefresh={handleRefresh}
              onPreviewSelect={handlePreviewRequest}
            />
          )}
          {activeTab === 'tasks' && (
            <TasksSection 
              trainerId={profile.id} 
              selectedPlan={selectedPlan} 
              highlightTaskId={selectedTaskId}
              onRefresh={handleRefresh}
              refreshKey={refreshTrigger}
              onPreviewRequest={handlePreviewRequest}
            />
          )}
          {activeTab === 'notes' && (
            selectedPlan ? (
              <div className="space-y-6">
                <div className="bg-white p-6 rounded-[2.5rem] border border-gray-100 shadow-sm flex flex-col sm:flex-row justify-between items-center gap-4">
                  <div>
                    <h2 className="text-2xl font-black text-gray-900">Plan Notes & Journal</h2>
                    <p className="text-sm text-gray-500 font-medium">Viewing for {selectedPlan.training_clients?.dog_name || 'Selected Plan'}</p>
                  </div>
                  <button onClick={() => setActiveTab('plans')} className="text-xs font-black text-indigo-600 uppercase tracking-widest hover:underline">Switch Plan</button>
                </div>
                <PlanNotes planId={selectedPlan.id} userRole="trainer" />
              </div>
            ) : (
              <div className="text-center py-24 bg-white rounded-[3.5rem] border-2 border-dashed border-gray-100">
                <div className="w-20 h-20 bg-gray-50 rounded-3xl flex items-center justify-center mx-auto mb-6">
                  <SafeIcon name="FileText" className="w-10 h-10 text-gray-300" />
                </div>
                <h3 className="text-2xl font-black text-gray-400">No Plan Selected</h3>
                <p className="text-gray-400 font-medium mt-2">Select a plan from the directory to manage notes</p>
                <button onClick={() => setActiveTab('plans')} className="mt-8 bg-indigo-600 text-white px-8 py-3 rounded-2xl font-black shadow-xl shadow-indigo-100 hover:scale-105 transition-all">Go to Plans</button>
              </div>
            )
          )}
          {activeTab === 'templates' && <TemplatesSection trainerId={profile.id} />}
          {activeTab === 'resources' && <ResourcesSection trainerId={profile.id} />}
          {activeTab === 'messaging' && (
            <MessagingSection 
              trainerId={profile.id} 
              trainerName={profile.first_name} 
              onUnreadUpdate={updateBadgeCount}
              initialUnreadMap={unreadMap}
            />
          )}
          {activeTab === 'payments' && <PaymentsSection trainerId={profile.id} />}
          {activeTab === 'settings' && <SettingsSection trainerId={profile.id} />}
        </div>

        <OwnerPreviewModal 
          isOpen={!!previewPlan} 
          onClose={() => { setPreviewPlan(null); setPreviewTasks([]); setPreviewClient(null); }}
          plan={previewPlan}
          tasks={previewTasks}
          client={previewClient}
          onUpdate={handleRefresh}
        />
      </div>
    </AuthGuard>
  );
};

export default TrainerDashboard;